import { BaseDomainModel } from '../models/base/base-domain-model';
import { inject, Injectable } from '@angular/core';
import { MembersAPI } from '../api/members-api';
import { map, switchMap, take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MemberUser } from '../models/users/dto/member-user';
import { SessionService } from '../services/session-service';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ChangeEmailRequest } from '../models/users/requests/change-email-request';
import { HttpResponse } from '@angular/common/http';
import { MemberUserSearchResult } from '../models/users/dto/member-user-search-result';
import { MemberRecordCommentRequest } from '../models/users/requests/member-record-comment-request';
import { MemberRecordComment } from '../views/records/components/members/ip-view-member-record/components/ip-member-record-comments/ip-member-record-comment';
import { Mentionable } from '../models/base/mentionable';
import { ODataResponse } from '../models/protocols/odata-response';
import { PortalService } from '../services/portal/portal.service';
import { PortalType } from '../models/enum/shared/portal-type';
import { EmploymentHistory } from '../models/users/dto/employment-history';
import { ResendMemberInviteRequest } from '../models/users/requests/resend-member-invite-request';
import { MostRecentInvite } from '../models/users/dto/most-recent-invite';
import { MemberRecord } from '../models/users/dto/member-record';
import { IpMemberSpouseContactInfoRequest } from '../models/users/requests/ip-member-spouse-contact-info-request';
import { MemberRelationshipStatus } from '../models/users/dto/member-relationship-status';
import { MemberOnboardingProgress } from '../models/members/member-onboarding-progress';
import { MemberPersonalInformationOnboardingRequest } from '../models/users/requests/member-personal-information-onboarding-request';
import { NewMemberVerificationRequest } from '../models/members/new-member-verification-request';
import { NewMemberVerificationResponse } from '../models/members/new-member-verification-response';
import { CreateMemberRequest } from '../models/users/requests/create-member-request';
import { ActiveRemittance } from '../models/remittances/active-remittance';
import { OdataUtils } from '../utils/odata-utils';

// Provided by Logged In Scope
@Injectable()
export class MembersDomainModel extends BaseDomainModel {
  constructor() {
    super();
    this.init();
  }

  private membersAPI = inject(MembersAPI);
  private session = inject(SessionService);
  private portalService = inject(PortalService);

  public addCommentToMemberRecord(req: MemberRecordCommentRequest): Observable<MemberRecordComment[]> {
    return this.membersAPI.addCommentToMemberRecord(req).pipe(
      map(c => {
        return c;
      })
    );
  }

  public updateMemberOnboardingInformation(req: MemberPersonalInformationOnboardingRequest): Observable<MemberUser> {
    return this.membersAPI.updateMemberOnboardingInformation(req).pipe(
      map(u => {
        this.session.updateUser(u);
        return u;
      })
    );
  }

  public memberPortalGetMember(odataQueryOptions: ODataQueryOptions): Observable<MemberUser> {
    return this.membersAPI.mpGetMember(odataQueryOptions);
  }

  public employerPortalGetMemberById(
    memberId: number,
    odataQueryOptions: ODataQueryOptions = new ODataQueryOptions()
  ): Observable<MemberUser> {
    return this.membersAPI.getEmployerMemberById(memberId, odataQueryOptions);
  }

  public ipGetMemberById(memberId: number, odataQueryOptions: ODataQueryOptions): Observable<MemberRecord> {
    return this.membersAPI.ipGetMemberById(memberId, odataQueryOptions);
  }

  public getMemberCommentMentionables(odataQueryOptions: ODataQueryOptions): Observable<Mentionable[]> {
    return this.membersAPI.getMemberCommentMentionables(odataQueryOptions).pipe(map(r => r.value));
  }

  public getMemberOnboardingProgress(opts: ODataQueryOptions): Observable<MemberOnboardingProgress> {
    return this.membersAPI.getMemberOnboardingProgress(opts);
  }

  public ipGetActiveRemittancesForMember(memberId: number): Observable<ActiveRemittance[]> {
    return this.membersAPI.ipGetActiveRemittancesForMember(memberId);
  }

  public forceChangeEmail(u: MemberUser, req: ChangeEmailRequest): Observable<MemberUser> {
    return this.membersAPI.forceChangeEmail(u, req);
  }

  public forceChangePassword(memberId: string): Observable<HttpResponse<any>> {
    return this.membersAPI.forceChangePassword(memberId);
  }

  public searchMembersByIdOrName(
    searchString: string,
    employerId?: number,
    top?: number
  ): Observable<MemberUserSearchResult[]> {
    const queryOptions = new ODataQueryOptions();
    const searchStringIsId = !isNaN(Number(searchString));
    if (searchStringIsId) {
      queryOptions.setFilter(`id eq ${searchString}`);
    } else {
      queryOptions.setFilter(`contains(Name, '${OdataUtils.escapeFilterValue(searchString)}')`);
    }
    if (top !== undefined) {
      queryOptions.setTop(top);
    }

    return this.portalService.isInternal$.pipe(
      switchMap(isInternal => {
        if (isInternal) {
          return this.membersAPI.ipSearchMembersByIdOrName(queryOptions, employerId).pipe(map(r => r.value));
        } else {
          return this.membersAPI.epSearchMembersByIdOrName(queryOptions, employerId).pipe(map(r => r.value));
        }
      })
    );
  }

  public getAllMembers(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberUser>> {
    return this.portalService.portalType$.pipe(
      take(1),
      switchMap(portalType => {
        switch (portalType) {
          case PortalType.Member:
          case PortalType.Employer:
            return this.membersAPI.getAllMembers(odataQueryOptions);
          case PortalType.Internal:
            return this.membersAPI.getAllInternalMembers(odataQueryOptions);
        }
      })
    );
  }

  public createEmployerHistory(req: EmploymentHistory): Observable<EmploymentHistory> {
    return this.membersAPI.createEmployerHistory(req, req.memberId.toString());
  }

  public resendMemberInvite(memberId: string, req: ResendMemberInviteRequest): Observable<MostRecentInvite> {
    return this.membersAPI.resendMemberInvite(memberId, req);
  }

  public ipUpdateMemberSpouseContactInfo(req: IpMemberSpouseContactInfoRequest): Observable<MemberRelationshipStatus> {
    return this.membersAPI.IpUpdateMemberSpouseContactInfo(req);
  }

  public ipGetNewMembersToVerify(req: NewMemberVerificationRequest[]): Observable<NewMemberVerificationResponse[]> {
    return this.membersAPI.ipGetNewMembersToVerify(req);
  }

  public ipCreateMembers(req: CreateMemberRequest[]): Observable<MemberUser[]> {
    return this.membersAPI.ipCreateNewMembers(req);
  }

  public epCreateEmployees(req: CreateMemberRequest[]): Observable<HttpResponse<any>> {
    return this.membersAPI.epCreateEmployees(req);
  }
}
